import { IonContent, useIonRouter } from '@ionic/react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
  healthCriticalIconTileClass,
} from '@up2stay/up2promo-ui';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { shellPaths } from '@/core/router/app-paths';

import { useWorkspaces } from '@/core/hooks/use-workspaces';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { AppIonPage } from '@/core/components/app-ion-page';

const TITLE_CLASS = 'text-[15.5px] font-bold leading-tight text-foreground';
const BODY_CLASS = 'text-[13.5px] leading-relaxed text-muted-foreground';

export function ErrorPage() {
  const router = useIonRouter();
  const dict = useDictionary();
  const location = useLocation();
  const { refetch, isLoading } = useWorkspaces();
  const [isRetrying, setIsRetrying] = useState(false);

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const from = params.get('from');

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await refetch();
      router.push(from || shellPaths.home, 'root', 'replace');
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <AppIonPage>
      <IonContent fullscreen>
        <div className="flex min-h-full items-center justify-center bg-background p-4">
          <Card className="w-full max-w-md overflow-hidden">
            <CardHeader className="gap-3 p-5 text-center sm:p-6">
              <div
                className={cn(
                  'mx-auto flex h-12 w-12 items-center justify-center rounded-2xl',
                  healthCriticalIconTileClass,
                )}
              >
                <AlertTriangle className="h-6 w-6" aria-hidden />
              </div>
              <CardTitle className={TITLE_CLASS}>{dict.error.title}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3 px-5 pb-5 text-center sm:px-6 sm:pb-6">
              <p className={BODY_CLASS}>{dict.error.description}</p>
              <Button
                variant="primary"
                className="w-full"
                onClick={() => {
                  void handleRetry();
                }}
                disabled={isRetrying || isLoading}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                {dict.error.tryAgain}
              </Button>
              <Button
                className="w-full"
                variant="outline"
                onClick={() => router.push(shellPaths.home, 'root', 'replace')}
              >
                <Home className="mr-2 h-4 w-4" />
                {dict.error.goToDashboard}
              </Button>
            </CardContent>
          </Card>
        </div>
      </IonContent>
    </AppIonPage>
  );
}
